import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { BaseAsyncService } from '@core/state/base-service';
import { DeceasedStore } from '@core/state/deceased.store';
import {
  DeceasedDetailsDto,
  DeceasedDocumentDto,
  DeceasedDto,
  InternationalLawInfoCreateUpdateRequest,
  InternationalLawInfoDto,
  ProblemDetails
} from '@shared/models/dto/api-dtos';

@Injectable({ providedIn: 'root' })
export class DeceasedService extends BaseAsyncService {
  private readonly deceasedStore = inject(DeceasedStore);
  private readonly BASE_ENDPOINT = '/deceased';
  private readonly requestConfig = {
    retry: this.createRetryConfig(1, 800, 1.5),
    logErrors: true
  };

  /**
   * Fetches the complete deceased entry including international law info and documents.
   */
  getDeceased(id: number): Observable<DeceasedDto> {
    return this.executeWithStore<DeceasedDto>(
      () => this.get<DeceasedDto>(`${this.BASE_ENDPOINT}/${id}`, this.requestConfig),
      deceased => this.deceasedStore.setDeceased(deceased),
      `Impossible de récupérer le défunt (id: ${id}).`,
      () => this.deceasedStore.setDeceased(null)
    );
  }

  /**
   * Fetches deceased details without international law info or documents.
   */
  getDeceasedDetails(id: string): Observable<DeceasedDetailsDto> {
    return this.executeWithStore<DeceasedDetailsDto>(
      () => this.get<DeceasedDetailsDto>(`${this.BASE_ENDPOINT}/${id}/details`, this.requestConfig),
      details => this.deceasedStore.setDetails(details),
      `Impossible de récupérer les détails du défunt (id: ${id}).`,
      () => this.deceasedStore.setDetails(null)
    );
  }

  /**
   * Retrieves the international law info for a deceased entry.
   */
  getInternationalLawInfo(id: string): Observable<InternationalLawInfoDto> {
    return this.executeWithStore<InternationalLawInfoDto>(
      () => this.get<InternationalLawInfoDto>(`${this.BASE_ENDPOINT}/${id}/internationalLawInfo`, this.requestConfig),
      info => this.deceasedStore.setInternationalLawInfo(info),
      `Impossible de récupérer les informations internationales du défunt (id: ${id}).`,
      () => this.deceasedStore.setInternationalLawInfo(null)
    );
  }

  /**
   * Creates or updates the international law info for a deceased entry.
   */
  saveInternationalLawInfo(
    id: string,
    payload: InternationalLawInfoCreateUpdateRequest
  ): Observable<InternationalLawInfoDto> {
    return this.executeWithStore<InternationalLawInfoDto>(
      () => this.post<InternationalLawInfoDto>(`${this.BASE_ENDPOINT}/${id}/internationalLawInfo`, payload, this.requestConfig),
      info => this.deceasedStore.setInternationalLawInfo(info),
      `Impossible d'enregistrer les informations internationales du défunt (id: ${id}).`
    );
  }

  /**
   * Retrieves the documents associated with the deceased entry.
   */
  getDeceasedDocuments(id: string): Observable<DeceasedDocumentDto[]> {
    return this.executeWithStore<DeceasedDocumentDto[]>(
      () => this.get<DeceasedDocumentDto[]>(`${this.BASE_ENDPOINT}/${id}/documents`, this.requestConfig),
      documents => this.deceasedStore.setDocuments(documents),
      `Impossible de récupérer les documents du défunt (id: ${id}).`,
      () => this.deceasedStore.setDocuments([])
    );
  }

  private executeWithStore<T>(
    requestFactory: () => Observable<T>,
    onSuccess: (response: T) => void,
    fallbackMessage: string,
    onNotFound?: () => void
  ): Observable<T> {
    this.deceasedStore.setLoading(true);
    this.deceasedStore.setError(null);

    return requestFactory().pipe(
      tap(onSuccess),
      catchError(error => this.handleError(error, fallbackMessage, onNotFound)),
      finalize(() => this.deceasedStore.setLoading(false))
    );
  }

  private handleError(
    error: ProblemDetails | HttpErrorResponse,
    fallbackMessage: string,
    onNotFound?: () => void
  ) {
    const status = (error as ProblemDetails)?.status ?? (error as HttpErrorResponse)?.status;
    if (status === 404) {
      onNotFound?.();
    }

    const message =
      (error as ProblemDetails)?.detail ||
      (error as ProblemDetails)?.title ||
      (error as HttpErrorResponse)?.message ||
      fallbackMessage;

    this.deceasedStore.setError(message || fallbackMessage);
    return throwError(() => error);
  }
}


